import { useState } from 'react'
import { useSessionUser } from '@/hooks/use-session'
import { useLingui } from '@lingui/react/macro'
import { skipToken, useMutation, useQuery } from '@tanstack/react-query'
import { ShieldIcon, LockOpenIcon, LockIcon, CheckIcon, PlusIcon, XIcon, BuildingIcon } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Avatar } from '../shared/avatar'
import { trpc } from '@/main'
import { Spinner } from '@/components/ui/spinner'
import { SearchInput } from '@/components/ui/search-input'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { toast } from '@/lib/toast'
import { ButtonPrimitive } from '@/components/ui/button-primitive'

// Controlled dialog. The opening menu item lives in user-menu.tsx and renders the
// dialog OUTSIDE the dropdown — opening it via a menu item's DialogTrigger fights the
// menu's modal/dismiss layer (open/close race), so the parent owns the open state.
// The body lives inside DialogContent, which base-ui unmounts while closed, so its session read,
// queries and mutations exist only while the dialog is open (the search box resets on reopen).
export function ProfileVisibilityDialog(props: {
  jobId?: number
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      {/* forceBackdrop: on mobile this opens from INSIDE the user-menu sheet, which stays visible behind it —
          base-ui renders no backdrop for a nested dialog, so without this nothing dims or blurs the sheet. */}
      <DialogContent forceBackdrop className="flex flex-col gap-2 sm:max-w-[480px]">
        <ProfileVisibilityDialogBody jobId={props.jobId} />
      </DialogContent>
    </Dialog>
  )
}

function ProfileVisibilityDialogBody(props: { jobId?: number }) {
  const { t } = useLingui()
  const [search, setSearch] = useState('')

  const user = useSessionUser()
  // query: direct visibility dialog: mounted only while open, skipToken unless it is a job's
  const { data: job } = useQuery(
    trpc.jobs.getJob.queryOptions(props.jobId != null ? { jobId: props.jobId } : skipToken),
  )

  const {
    mutateAsync: putJobVisibility,
    variables: jobVariables,
    isPending: jobVisibilityPending,
  } = useMutation(trpc.companies.putOfferVisibility.mutationOptions())

  const {
    mutateAsync: putVisibility,
    variables,
    isPending: userVisibilityPending,
  } = useMutation(trpc.auth.putVisibility.mutationOptions())

  const visibility = props.jobId ? (jobVariables?.visibility ?? job?.visibility) : (variables ?? user?.visibility)
  const visibilityPending = props.jobId ? jobVisibilityPending : userVisibilityPending

  const handleVisibility = async (isPublic: boolean) => {
    if (props.jobId) {
      await putJobVisibility({ offerId: props.jobId, visibility: isPublic })
    } else {
      await putVisibility(isPublic)
    }
  }

  // Allowed users
  // query: direct visibility dialog: mounted only while open, skipToken for the other target
  const { data: allowedUsers = [], isLoading: allowedUsersLoading } = useQuery(
    trpc.auth.getAllowedUsers.queryOptions(props.jobId != null ? skipToken : undefined),
  )
  // query: direct visibility dialog: mounted only while open, skipToken for the other target
  const { data: allowedJobUsers = [], isLoading: allowedJobUsersLoading } = useQuery(
    trpc.companies.getOfferAllowedUsers.queryOptions(props.jobId ?? skipToken),
  )

  const allowedList = props.jobId ? allowedJobUsers : allowedUsers
  const allowedListLoading = props.jobId ? allowedJobUsersLoading : allowedUsersLoading

  const {
    mutateAsync: allowUser,
    isPending: allowUserIsPending,
    variables: allowUserVariables,
  } = useMutation(trpc.auth.allowUser.mutationOptions())
  const {
    mutateAsync: allowJobUser,
    isPending: allowJobUserIsPending,
    variables: allowJobUserVariables,
  } = useMutation(trpc.companies.allowOfferUser.mutationOptions())
  const {
    mutateAsync: deleteAllowedUser,
    isPending: deleteAllowedUserIsPending,
    variables: deleteAllowedUserVariables,
  } = useMutation(trpc.auth.deleteAllowedUser.mutationOptions())
  const {
    mutateAsync: deleteAllowedJobUser,
    isPending: deleteAllowedJobUserIsPending,
    variables: deleteAllowedJobUserVariables,
  } = useMutation(trpc.companies.deleteAllowedOfferUser.mutationOptions())

  const currentAllowedUserId = allowUserVariables ?? allowJobUserVariables?.userId
  const currentAllowedUserIdIsPending = allowUserIsPending || allowJobUserIsPending
  const currentDeletedUserId = deleteAllowedUserVariables ?? deleteAllowedJobUserVariables?.userId
  const currentDeletedUserIdIsPending = deleteAllowedJobUserIsPending || deleteAllowedUserIsPending

  const handleAllowedUser = async (userId: number) => {
    if (props.jobId) {
      await allowJobUser({ offerId: props.jobId, userId })
    } else {
      await allowUser(userId)
    }
    toast.success(t`Company added successfully`)
    setSearch('')
  }

  const handleDeleteAllowedUser = async (userId: number) => {
    if (props.jobId) {
      await deleteAllowedJobUser({ offerId: props.jobId, userId })
    } else {
      await deleteAllowedUser(userId)
    }
    toast.success(t`Company removed successfully`)
  }

  // Search
  // query: direct live search inside the dialog — the key is what the user just typed
  const { data: searchResults, isPending: searchPending } = useQuery(
    trpc.common.searchToAddToAllowedUsers.queryOptions(
      // Personal profile visibility (no jobId) only allows companies as viewers
      search === '' ? skipToken : { q: search, onlyCompanies: !props.jobId },
    ),
  )

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <ShieldIcon className="text-primary size-5" />
          {t`Profile Visibility`}
        </DialogTitle>
        <DialogDescription>{t`Decide who can see and compare your profile`}</DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-2">
        <ButtonPrimitive
          type="button"
          onClick={() => handleVisibility(true)}
          disabled={visibilityPending}
          className={cn(
            'group flex cursor-pointer items-center gap-4 rounded-xl border-2 p-4 text-left transition-colors',
            visibility
              ? 'border-primary/30 bg-primary/5'
              : 'border-transparent bg-muted/30 hover:border-muted-foreground/20 hover:bg-muted/50',
          )}
        >
          <div
            className={cn(
              'flex size-12 items-center justify-center rounded-full transition-colors',
              visibility ? 'bg-primary/10' : 'bg-muted',
            )}
          >
            <LockOpenIcon className={cn('size-6', visibility ? 'text-primary' : 'text-muted-foreground')} />
          </div>
          <div className="flex-1">
            <p className={cn('font-semibold', visibility ? 'text-foreground' : 'text-muted-foreground')}>
              {t`Public Profile`}
            </p>
            <p className="text-muted-foreground text-xs">{t`Anyone will be able to view and compare your profile`}</p>
          </div>
          {visibility && (
            <div className="bg-primary flex size-6 items-center justify-center rounded-full">
              <CheckIcon className="text-primary-foreground size-4" />
            </div>
          )}
        </ButtonPrimitive>

        <ButtonPrimitive
          type="button"
          onClick={() => handleVisibility(false)}
          disabled={visibilityPending}
          className={cn(
            'group flex cursor-pointer items-center gap-4 rounded-xl border-2 p-4 text-left transition-colors',
            !visibility
              ? 'border-primary/30 bg-primary/5'
              : 'border-transparent bg-muted/30 hover:border-muted-foreground/20 hover:bg-muted/50',
          )}
        >
          <div
            className={cn(
              'flex size-12 items-center justify-center rounded-full transition-colors',
              !visibility ? 'bg-primary/10' : 'bg-muted',
            )}
          >
            <LockIcon className={cn('size-6', !visibility ? 'text-primary' : 'text-muted-foreground')} />
          </div>
          <div className="flex-1">
            <p className={cn('font-semibold', !visibility ? 'text-foreground' : 'text-muted-foreground')}>
              {t`Private Profile`}
            </p>
            <p className="text-muted-foreground text-xs">{t`Only the companies you select will be able to view and compare your profile`}</p>
          </div>
          {!visibility && (
            <div className="bg-primary flex size-6 items-center justify-center rounded-full">
              <CheckIcon className="text-primary-foreground size-4" />
            </div>
          )}
        </ButtonPrimitive>
      </div>

      {/* Private mode: allowed users section */}
      {!visibility && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h4 className="flex items-center gap-2 text-sm font-semibold">
              <BuildingIcon className="text-muted-foreground size-4" />
              {t`Authorized companies`}
            </h4>
            <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs">
              {allowedList.length}
            </span>
          </div>

          <SearchInput placeholder={t`Search companies to add...`} value={search} onChange={setSearch} />

          {search !== '' && (
            <div className="bg-muted/20 rounded-lg border p-2">
              {searchPending ? (
                <div className="flex items-center justify-center py-4">
                  <Spinner className="text-primary" />
                </div>
              ) : searchResults?.list.length === 0 ? (
                <p className="text-muted-foreground py-4 text-center text-sm">{t`No results found`}</p>
              ) : (
                <div className="flex flex-col gap-1">
                  {searchResults?.list.map((u) => {
                    const isAlreadyAdded = allowedList.some((a) => a.id === u.id)
                    return (
                      <div
                        key={u.id}
                        className="hover:bg-muted/50 flex items-center gap-3 rounded-lg p-2 transition-colors"
                      >
                        <Avatar size="lg" url={u.image} type={u.type} name={u.name} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{u.name}</p>
                          {u.job && <p className="text-muted-foreground truncate text-xs">{u.job}</p>}
                        </div>
                        {isAlreadyAdded ? (
                          <span className="text-muted-foreground flex items-center gap-1 text-xs">
                            <CheckIcon className="text-primary size-3.5" />
                            {t`Added`}
                          </span>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAllowedUser(u.id)}
                            disabled={currentAllowedUserIdIsPending && currentAllowedUserId === u.id}
                            className="h-8 gap-1.5"
                          >
                            {currentAllowedUserIdIsPending && currentAllowedUserId === u.id ? (
                              <Spinner className="size-3.5" />
                            ) : (
                              <PlusIcon className="size-3.5" />
                            )}
                            {t`Add`}
                          </Button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          <div className="max-h-48 overflow-y-auto">
            {allowedListLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner className="text-primary" />
              </div>
            ) : allowedList.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
                <BuildingIcon className="text-muted-foreground size-6" />
                <p className="text-muted-foreground text-sm">{t`No companies added yet`}</p>
              </div>
            ) : (
              <div className="flex flex-col">
                {allowedList.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 p-3">
                    <Avatar size="lg" url={u.image} type={u.type} name={u.name} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{u.name}</p>
                      {u.job && <p className="text-muted-foreground truncate text-xs">{u.job}</p>}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t`Remove access`}
                      onClick={() => handleDeleteAllowedUser(u.id)}
                      disabled={currentDeletedUserIdIsPending && currentDeletedUserId === u.id}
                      className="text-muted-foreground hover:text-destructive size-8"
                    >
                      {currentDeletedUserIdIsPending && currentDeletedUserId === u.id ? (
                        <Spinner className="size-3.5" />
                      ) : (
                        <XIcon className="size-4" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
